// Phase 19.19 — Template picker for new quotes.
// Users can start a fresh quote either blank or pre-loaded with a saved
// DocumentLayoutTemplate's section order + design. After creation, we redirect
// straight to the Live Editor (`/quotes/:id/editor?template={id}`) which
// auto-applies the template on mount.
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Loader2, Sparkles, FileText, Plus, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewQuoteFromTemplate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null); // tplId currently in-flight (or "blank")
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/doc-templates?doc_type=quote`);
        setTemplates(r.data || []);
      } catch {
        setTemplates([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const startQuote = async (tpl) => {
    const key = tpl ? tpl.id : "blank";
    setCreating(key);
    try {
      // Minimal-payload draft. The Live Editor lets the user fill the rest.
      const res = await axios.post(`${API}/quotes`, {
        manual_customer_name: "(new customer)",
        status: "draft",
        tax_rate: 0,
        items: [],
      });
      const quoteId = res.data?.id;
      if (!quoteId) {
        toast.error("Could not create the draft quote");
        return;
      }
      const url = tpl
        ? `/quotes/${quoteId}/editor?template=${tpl.id}`
        : `/quotes/${quoteId}/editor`;
      toast.success(tpl ? `Starting from "${tpl.name}"` : "Starting blank quote");
      navigate(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create draft");
    } finally {
      setCreating(null);
    }
  };

  return (
    <div className="space-y-6 animate-in" data-testid="new-quote-template-picker">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-primary" /> Start a new quote
          </h1>
          <p className="text-muted-foreground mt-1">
            Pick a saved template (preserves your section order, colors & logo) or start fresh.
          </p>
        </div>
        <Link to="/quotes">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Quotes
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Blank card */}
          <Card
            className="cursor-pointer hover:shadow-md transition-shadow border-dashed border-2"
            onClick={() => startQuote(null)}
            data-testid="start-blank-quote"
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Plus className="h-4 w-4" /> Blank quote
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              Default layout, no logo. You can add a template later.
              {creating === "blank" && (
                <div className="flex items-center text-primary mt-2 text-xs">
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Creating…
                </div>
              )}
            </CardContent>
          </Card>

          {templates.map((tpl) => (
            <Card
              key={tpl.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => startQuote(tpl)}
              data-testid={`start-from-template-${tpl.id}`}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4" /> {tpl.name}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                {tpl.design?.primary_color && (
                  <div className="flex items-center gap-2 text-xs">
                    <span
                      className="inline-block w-4 h-4 rounded border"
                      style={{ background: tpl.design.primary_color }}
                    />
                    <span className="font-mono">{tpl.design.primary_color}</span>
                    {tpl.design.accent_color && (
                      <>
                        <span
                          className="inline-block w-4 h-4 rounded border ml-1"
                          style={{ background: tpl.design.accent_color }}
                        />
                        <span className="font-mono">{tpl.design.accent_color}</span>
                      </>
                    )}
                  </div>
                )}
                {tpl.sections?.length > 0 && (
                  <div className="text-xs text-muted-foreground truncate">
                    {tpl.sections.length} sections: {tpl.sections.slice(0, 5).join(" → ")}
                    {tpl.sections.length > 5 ? "…" : ""}
                  </div>
                )}
                {tpl.design?.logo_url && (
                  <img
                    src={tpl.design.logo_url}
                    alt="logo"
                    style={{ maxHeight: 40, maxWidth: 120 }}
                    className="rounded"
                  />
                )}
                {creating === tpl.id && (
                  <div className="flex items-center text-primary mt-1 text-xs">
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Creating…
                  </div>
                )}
              </CardContent>
            </Card>
          ))}

          {templates.length === 0 && (
            <Card className="lg:col-span-2 bg-muted/30">
              <CardContent className="py-6 text-sm text-muted-foreground">
                No templates yet. Once you save a layout from the Live Editor, it&apos;ll show up here for one-click reuse.
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default NewQuoteFromTemplate;
